import React, { useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import NavBar from "../../components/NavBar";
import { createCatalogItem, updateCatalogItem } from "../../services/catalogClient";
import { parseNutritionText, scanNutritionLabelFromImage } from "../../utils/nutritionLabelOcr";
import { toMassValue } from "../../utils/nutrients";
import {
  clearMealDraftHandoff,
  loadMealDraftHandoff,
} from "../../utils/mealDraftHandoff";

const MACRO_FIELDS = [
  { key: "calories", label: "Calories", unit: "kcal" },
  { key: "protein", label: "Protein", unit: "g" },
  { key: "carbs", label: "Carbs", unit: "g" },
  { key: "fat", label: "Fat", unit: "g" },
  { key: "fiber", label: "Fiber", unit: "g" },
  { key: "sugar", label: "Sugar", unit: "g" },
  { key: "cholesterol", label: "Cholesterol", unit: "mg" },
];

const MICRO_FIELDS = [
  { key: "sodium", label: "Sodium" },
  { key: "potassium", label: "Potassium" },
  { key: "calcium", label: "Calcium" },
  { key: "iron", label: "Iron" },
  { key: "vitaminA", label: "Vitamin A" },
  { key: "vitaminC", label: "Vitamin C" },
];

const DEFAULT_MICROS = MICRO_FIELDS.reduce((acc, field) => {
  acc[field.key] = { value: "", unit: "mg" };
  return acc;
}, {});

const DEFAULT_MACROS = MACRO_FIELDS.reduce((acc, field) => {
  acc[field.key] = "";
  return acc;
}, {});

const SERVING_UNITS = ["g", "mg", "ml", "oz", "lb", "cup", "tbsp", "tsp", "piece"];
const MASS_EQUIVALENTS = {
  mg: 0.001,
  g: 1,
  ml: 1,
  oz: 28.3495,
  lb: 453.592,
  cup: 236.588,
  tbsp: 14.7868,
  tsp: 4.92892,
};

function Section({ title, children }) {
  return (
    <section style={{
      background: "#fffdf7",
      border: "1px solid rgba(22,50,39,0.12)",
      borderRadius: 18,
      padding: 20,
      display: "grid",
      gap: 16,
      overflow: "hidden",
      minWidth: 0,
    }}>
      <h3 style={{ margin: 0, color: "#163227" }}>{title}</h3>
      {children}
    </section>
  );
}

function Field({ label, children, hint }) {
  return (
    <label style={{ display: "grid", gap: 8 }}>
      <span style={{ fontWeight: 600, color: "#243E2D" }}>{label}</span>
      {children}
      {hint && <span style={{ fontSize: 12, color: "#5f6f64" }}>{hint}</span>}
    </label>
  );
}

function baseInputStyle() {
  return {
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    border: "1px solid rgba(22,50,39,0.18)",
    borderRadius: 12,
    padding: "12px 14px",
    fontSize: 15,
    background: "#fff",
    boxSizing: "border-box",
  };
}

function cleanDecimalInput(value) {
  return (value ?? "").replace(/[^0-9.]/g, "");
}

function getServingGrams(qty, unit) {
  const numericQty = Number(qty || 0);
  const gramsPerUnit = MASS_EQUIVALENTS[String(unit || "").trim().toLowerCase()];
  if (!(numericQty > 0) || !gramsPerUnit) return null;
  return numericQty * gramsPerUnit;
}

function normalizeMeasureRows(rows) {
  return (Array.isArray(rows) ? rows : [])
    .map((row) => ({
      qty: row?.qty === undefined || row?.qty === null ? "" : String(row.qty),
      unit: row?.unit || "cup",
      grams: row?.grams === undefined || row?.grams === null ? "" : String(row.grams),
    }))
    .filter((row) => row.qty !== "" || row.grams !== "");
}

function getSavedMeasureRows(unitConversions = {}) {
  const customMeasures = normalizeMeasureRows(unitConversions.custom_measures);
  if (customMeasures.length > 0) {
    return customMeasures;
  }

  return SERVING_UNITS
    .filter((unit) => unit !== "g" && Number(unitConversions[unit]) > 0)
    .map((unit) => ({
      qty: "1",
      unit,
      grams: String(unitConversions[unit]),
    }));
}

function getGramsPerUnitFromRows(rows, unit) {
  const normalizedUnit = String(unit || "").trim().toLowerCase();
  const match = rows.find((row) => row.unit === normalizedUnit && Number(row.qty) > 0 && Number(row.grams) > 0);
  if (!match) return null;
  return Number(match.grams) / Number(match.qty);
}

function readImageAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(new Error("Failed to read photo."));
    reader.readAsDataURL(file);
  });
}

export default function NewIngredientPage({ user }) {
  const location = useLocation();
  const navigate = useNavigate();
  const storedHandoff = loadMealDraftHandoff();
  const returnTo = location.state?.returnTo || storedHandoff?.returnTo || "/meals/new";
  const existingIngredient = location.state?.ingredient || null;
  const existingServing = existingIngredient?.unit_conversions?.serving_size || {};
  const existingConversions = existingIngredient?.unit_conversions || {};
  const existingMacros = existingIngredient?.unit_conversions?.macros || {};
  const existingMicros = existingIngredient?.unit_conversions?.micros || {};
  const existingPhoto = existingIngredient?.unit_conversions?.photo_data_url || "";
  const isEditing = Boolean(existingIngredient?.id);
  const photoInputRef = useRef(null);
  const [name, setName] = useState(
    existingIngredient?.title || location.state?.ingredientName || storedHandoff?.ingredientName || ""
  );
  const [brand, setBrand] = useState(existingIngredient?.unit_conversions?.brand || "");
  const [photoDataUrl, setPhotoDataUrl] = useState(existingPhoto);
  const [servingSize, setServingSize] = useState(
    existingServing.qty === undefined || existingServing.qty === null ? "" : String(existingServing.qty)
  );
  const [servingUnit, setServingUnit] = useState(existingServing.unit || "g");
  const [measureRows, setMeasureRows] = useState(() => getSavedMeasureRows(existingConversions));
  const [macros, setMacros] = useState({
    ...DEFAULT_MACROS,
    ...Object.fromEntries(
      Object.entries(existingMacros).map(([key, value]) => [key, value === undefined || value === null ? "" : String(value)])
    ),
  });
  const [micros, setMicros] = useState({
    ...DEFAULT_MICROS,
    ...Object.fromEntries(
      Object.entries(existingMicros).map(([key, value]) => [
        key,
        {
          value: value?.value === undefined || value?.value === null ? "" : String(value.value),
          unit: value?.unit || DEFAULT_MICROS[key]?.unit || "mg",
        },
      ])
    ),
  });
  const [msg, setMsg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [scanningPhoto, setScanningPhoto] = useState(false);
  const [nutritionPaste, setNutritionPaste] = useState("");

  function updateMacro(key, value) {
    setMacros((current) => ({ ...current, [key]: value }));
  }

  function updateMicroValue(key, value) {
    setMicros((current) => ({
      ...current,
      [key]: { ...current[key], value },
    }));
  }

  function updateMicroUnit(key, unit) {
    setMicros((current) => ({
      ...current,
      [key]: { ...current[key], unit },
    }));
  }

  function updateMeasureRow(index, changes) {
    setMeasureRows((current) => current.map((row, i) => (i === index ? { ...row, ...changes } : row)));
  }

  function addMeasureRow() {
    setMeasureRows((current) => [...current, { qty: "1", unit: "cup", grams: "" }]);
  }

  function removeMeasureRow(index) {
    setMeasureRows((current) => current.filter((_, i) => i !== index));
  }

  function applyParsedNutrition(parsed) {
    if (parsed?.serving?.qty) {
      setServingSize(String(parsed.serving.qty));
    }
    if (parsed?.serving?.unit && SERVING_UNITS.includes(parsed.serving.unit)) {
      setServingUnit(parsed.serving.unit);
    }

    setMacros((current) => ({
      ...current,
      ...Object.fromEntries(
        MACRO_FIELDS
          .filter((field) => parsed?.macros?.[field.key] != null)
          .map((field) => [field.key, String(parsed.macros[field.key])])
      ),
    }));

    setMicros((current) => ({
      ...current,
      ...Object.fromEntries(
        MICRO_FIELDS
          .filter((field) => parsed?.micros?.[field.key]?.value != null)
          .map((field) => [
            field.key,
            {
              value: String(parsed.micros[field.key].value),
              unit: parsed.micros[field.key].unit,
            },
          ])
      ),
    }));
  }

  function handleNutritionPaste() {
    const parsed = parseNutritionText(nutritionPaste);
    const recognizedMacros = MACRO_FIELDS.filter((field) => parsed?.macros?.[field.key] != null);
    const recognizedMicros = MICRO_FIELDS.filter((field) => parsed?.micros?.[field.key]?.value != null);
    const recognizedCount = recognizedMacros.length + recognizedMicros.length + (parsed?.serving?.qty ? 1 : 0);

    if (!recognizedCount) {
      setMsg('No labeled nutrition values were recognized. Try lines like "Protein 12 g" or "Sodium: 300 mg".');
      return;
    }

    applyParsedNutrition(parsed);
    setMsg(
      `Filled ${recognizedCount} nutrition ${recognizedCount === 1 ? "value" : "values"}. Review them before saving.`
    );
  }

  async function handlePhotoChange(event) {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      setScanningPhoto(true);
      setMsg("Reading nutrition label...");
      const dataUrl = await readImageAsDataUrl(file);
      setPhotoDataUrl(dataUrl);
      const parsed = await scanNutritionLabelFromImage(dataUrl);
      applyParsedNutrition(parsed);

      setMsg("Nutrition label scanned. Review the values before saving.");
    } catch (error) {
      setMsg(error.message || "Failed to load photo.");
    } finally {
      setScanningPhoto(false);
    }
  }

  async function handleSave() {
    if (!name.trim()) {
      setMsg("Ingredient name is required.");
      return;
    }

    if (!servingSize) {
      setMsg("Add a serving size.");
      return;
    }

    setSaving(true);

    const payload = {
      title: name.trim(),
      brand: brand.trim() || null,
      servingSize: Number(servingSize) || 0,
      servingUnit,
      macros: Object.fromEntries(
        Object.entries(macros).map(([key, value]) => [key, Number(value) || 0])
      ),
      micros: Object.fromEntries(
        Object.entries(micros).map(([key, value]) => [key, { value: Number(value.value) || 0, unit: value.unit }])
      ),
    };

    try {
      const cleanedMeasureRows = normalizeMeasureRows(measureRows);
      const invalidMeasure = cleanedMeasureRows.find((row) => !(Number(row.qty) > 0) || !(Number(row.grams) > 0));
      if (invalidMeasure) {
        throw new Error("Each alternate measure needs an amount and a gram weight.");
      }

      const unitConversions = Object.fromEntries(
        cleanedMeasureRows.map((row) => [row.unit, Number((Number(row.grams) / Number(row.qty)).toFixed(4))])
      );
      const customServingGramsPerUnit = getGramsPerUnitFromRows(cleanedMeasureRows, payload.servingUnit);
      const servingGrams =
        Number(payload.servingSize) > 0 && customServingGramsPerUnit
          ? Number(payload.servingSize) * customServingGramsPerUnit
          : getServingGrams(payload.servingSize, payload.servingUnit);
      if (!(servingGrams > 0)) {
        const unitLabel = payload.servingUnit === "piece" ? "piece" : payload.servingUnit;
        throw new Error(`Add an alternate measure that says how many grams are in 1 ${unitLabel}.`);
      }
      const toPer100g = (value) => {
        const numericValue = Number(value || 0);
        return numericValue * (100 / servingGrams);
      };

      const macrosPer100g = {
        calories: toPer100g(payload.macros.calories),
        protein: toPer100g(payload.macros.protein),
        carbs: toPer100g(payload.macros.carbs),
        fat: toPer100g(payload.macros.fat),
        fiber: toPer100g(payload.macros.fiber),
        sugar: toPer100g(payload.macros.sugar),
        cholesterol: toPer100g(payload.macros.cholesterol),
      };

      const microsPer100g = Object.fromEntries(
        Object.entries(payload.micros).map(([key, value]) => [key, toPer100g(toMassValue(value.value, value.unit, key))])
      );

      const normalizedMicros = Object.fromEntries(
        Object.entries(payload.micros).map(([key, value]) => {
          const numericValue = toMassValue(value.value, value.unit, key);
          const unit = key === "vitaminA" ? "mcg" : "mg";
          return [key, { value: Number(numericValue.toFixed(2)), unit }];
        })
      );

      const catalogPayload = {
        title: payload.title,
        item_type: "ingredient",
        kcal_per_100g: Number(macrosPer100g.calories.toFixed(2)),
        protein_g_per_100g: Number(macrosPer100g.protein.toFixed(2)),
        carbs_g_per_100g: Number(macrosPer100g.carbs.toFixed(2)),
        fat_g_per_100g: Number(macrosPer100g.fat.toFixed(2)),
        unit_conversions: {
          ...unitConversions,
          brand: payload.brand,
          serving_size: {
            qty: payload.servingSize,
            unit: payload.servingUnit,
          },
          custom_measures: cleanedMeasureRows.map((row) => ({
            qty: Number(row.qty),
            unit: row.unit,
            grams: Number(row.grams),
          })),
          macros: payload.macros,
          macros_per_100g: {
            fiber: Number(macrosPer100g.fiber.toFixed(2)),
            sugar: Number(macrosPer100g.sugar.toFixed(2)),
            cholesterol: Number(macrosPer100g.cholesterol.toFixed(2)),
          },
          micros: normalizedMicros,
          micros_per_100g: Object.fromEntries(
            Object.entries(microsPer100g).map(([key, value]) => [key, Number(value.toFixed(2))])
          ),
        },
      };

      const savedIngredient = isEditing
        ? await updateCatalogItem(existingIngredient.id, catalogPayload)
        : await createCatalogItem(catalogPayload);
      const mealDraft = location.state?.mealDraft || storedHandoff?.mealDraft || null;
      const returnPasteRowId = location.state?.returnPasteRowId || storedHandoff?.returnPasteRowId;
      const returnedMealDraft = returnPasteRowId && mealDraft
        ? {
            ...mealDraft,
            ingredientPasteDraft: {
              ...mealDraft.ingredientPasteDraft,
              showPaste: true,
              createdIngredient: savedIngredient,
              createdForRowId: returnPasteRowId,
            },
          }
        : mealDraft;

      setSaving(false);
      clearMealDraftHandoff();
      navigate(returnTo, {
        replace: true,
        state: { mealDraft: returnedMealDraft },
      });
    } catch (error) {
      setSaving(false);
      setMsg(error.message || String(error));
    }
  }

  return (
    <main
      className="page form-page"
      style={{
        minHeight: "100vh",
        background: "transparent",
        padding: "32px 20px 48px",
      }}
    >
      <NavBar profileImageSrc={user?.avatar} />
      <div style={{ maxWidth: 960, margin: "0 auto", display: "grid", gap: 20 }}>
        <header className="cc-page-heading">
          <h2 className="cc-page-title">
            {isEditing ? "Edit Ingredient" : "New Ingredient"}
          </h2>
          <p className="cc-page-subtitle">
            {isEditing
              ? "Update the nutrient profile and serving details for this ingredient."
              : "Add the full nutrient profile and the serving size those values refer to."}
          </p>
        </header>

        <Section title="Ingredient Details">
          <div style={{ display: "grid", gap: 16, gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", minWidth: 0 }}>
            <Field label="Ingredient Name">
              <input
                style={baseInputStyle()}
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Greek Yogurt"
              />
            </Field>

            <Field label="Brand / Source">
              <input
                style={baseInputStyle()}
                value={brand}
                onChange={(e) => setBrand(e.target.value)}
                placeholder="Optional"
              />
            </Field>
          </div>

          <Field label="Photo" hint="Take a picture or upload one from your phone.">
            <div style={{ display: "grid", gap: 12, minWidth: 0 }}>
              <input
                ref={photoInputRef}
                type="file"
                accept="image/*"
                onChange={handlePhotoChange}
                style={{ display: "none" }}
              />
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                <button
                  type="button"
                  onClick={() => photoInputRef.current?.click()}
                  style={{
                    borderRadius: 999,
                    padding: "12px 18px",
                    background: "#163227",
                    color: "#fff",
                    border: 0,
                    fontWeight: 700,
                    cursor: "pointer",
                  }}
                >
                  {scanningPhoto ? "Reading Photo..." : "Upload Photo"}
                </button>
                {photoDataUrl ? (
                  <button
                    type="button"
                    onClick={() => setPhotoDataUrl("")}
                    style={{
                      borderRadius: 999,
                      padding: "12px 18px",
                      background: "transparent",
                      color: "#163227",
                      border: "1px solid rgba(22,50,39,0.18)",
                      fontWeight: 700,
                      cursor: "pointer",
                    }}
                  >
                    Remove Photo
                  </button>
                ) : null}
              </div>
              {photoDataUrl ? (
                <img
                  src={photoDataUrl}
                  alt="Ingredient preview"
                  style={{
                    width: "100%",
                    maxWidth: 220,
                    aspectRatio: "1 / 1",
                    objectFit: "cover",
                    borderRadius: 16,
                    border: "1px solid rgba(22,50,39,0.12)",
                  }}
                />
              ) : null}
            </div>
          </Field>
        </Section>

        <Section title="Paste Nutrition Values">
          <div style={{ display: "grid", gap: 12 }}>
            <p style={{ margin: 0, color: "#5f6f64" }}>
              Paste the nutrition values for this ingredient in any order. You can copy a nutrition label, use commas, or put one value on each line.
            </p>
            <textarea
              style={{
                ...baseInputStyle(),
                minHeight: 150,
                resize: "vertical",
                lineHeight: 1.5,
              }}
              value={nutritionPaste}
              onChange={(event) => setNutritionPaste(event.target.value)}
              placeholder={"Serving size: 30 g\nProtein 12 g, Calories 180\nSodium: 300 mg\nCarbs 8 g\nFat 9 g"}
              aria-label="Paste nutrition values"
            />
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <button
                type="button"
                onClick={handleNutritionPaste}
                disabled={!nutritionPaste.trim()}
                style={{
                  borderRadius: 999,
                  padding: "12px 18px",
                  background: nutritionPaste.trim() ? "#163227" : "#9aa59f",
                  color: "#fff",
                  border: 0,
                  fontWeight: 700,
                  cursor: nutritionPaste.trim() ? "pointer" : "not-allowed",
                }}
              >
                Fill Nutrition Fields
              </button>
              {nutritionPaste ? (
                <button
                  type="button"
                  onClick={() => setNutritionPaste("")}
                  style={{
                    borderRadius: 999,
                    padding: "12px 18px",
                    background: "transparent",
                    color: "#163227",
                    border: "1px solid rgba(22,50,39,0.18)",
                    fontWeight: 700,
                    cursor: "pointer",
                  }}
                >
                  Clear
                </button>
              ) : null}
            </div>
          </div>
        </Section>

        <Section title="Serving Size">
          <div style={{ display: "grid", gap: 16, gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", minWidth: 0 }}>
            <Field label="Serving Size" hint="What nutrition values below refer to.">
              <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 10, minWidth: 0 }}>
                <input
                  style={baseInputStyle()}
                  type="text"
                  inputMode="decimal"
                  value={servingSize}
                  onChange={(e) => setServingSize(cleanDecimalInput(e.target.value))}
                  placeholder="100"
                />
                <select
                  style={baseInputStyle()}
                  value={servingUnit}
                  onChange={(e) => setServingUnit(e.target.value)}
                >
                  {SERVING_UNITS.map((unit) => (
                    <option key={unit} value={unit}>{unit}</option>
                  ))}
                </select>
              </div>
            </Field>
          </div>
        </Section>

        <Section title="Alternate Measures">
          <div style={{ display: "grid", gap: 12 }}>
            <p style={{ margin: 0, color: "#5f6f64" }}>
              Add gram equivalents for ways you measure this ingredient, such as 1 cup = 240 g, 0.25 cup = 60 g, or 1 piece = 50 g.
            </p>
            {measureRows.map((row, index) => (
              <div
                key={`${row.unit}-${index}`}
                style={{
                  display: "grid",
                  gridTemplateColumns: "minmax(80px, 1fr) minmax(100px, 1fr) auto minmax(90px, 1fr) auto",
                  gap: 10,
                  alignItems: "center",
                  minWidth: 0,
                }}
              >
                <input
                  style={baseInputStyle()}
                  type="text"
                  inputMode="decimal"
                  value={row.qty}
                  onChange={(e) => updateMeasureRow(index, { qty: cleanDecimalInput(e.target.value) })}
                  placeholder="1"
                  aria-label="Measure amount"
                />
                <select
                  style={baseInputStyle()}
                  value={row.unit}
                  onChange={(e) => updateMeasureRow(index, { unit: e.target.value })}
                  aria-label="Measure unit"
                >
                  {SERVING_UNITS.filter((unit) => unit !== "g").map((unit) => (
                    <option key={unit} value={unit}>{unit}</option>
                  ))}
                </select>
                <span style={{ color: "#516257", fontWeight: 700 }}>equals</span>
                <input
                  style={baseInputStyle()}
                  type="text"
                  inputMode="decimal"
                  value={row.grams}
                  onChange={(e) => updateMeasureRow(index, { grams: cleanDecimalInput(e.target.value) })}
                  placeholder="0"
                  aria-label="Gram weight"
                />
                <button
                  type="button"
                  onClick={() => removeMeasureRow(index)}
                  style={{
                    borderRadius: 999,
                    padding: "10px 14px",
                    background: "transparent",
                    border: "1px solid rgba(22,50,39,0.18)",
                    color: "#163227",
                    fontWeight: 700,
                    cursor: "pointer",
                  }}
                >
                  Remove
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={addMeasureRow}
              style={{
                justifySelf: "start",
                borderRadius: 999,
                padding: "12px 18px",
                background: "#eef5e8",
                color: "#163227",
                border: "1px solid rgba(22,50,39,0.12)",
                fontWeight: 700,
                cursor: "pointer",
              }}
            >
              Add Measure
            </button>
          </div>
        </Section>

        <Section title="Macros">
          <div style={{ display: "grid", gap: 16, gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", minWidth: 0 }}>
            {MACRO_FIELDS.map((field) => (
              <Field key={field.key} label={field.label}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 10, alignItems: "center", minWidth: 0 }}>
                  <input
                    style={baseInputStyle()}
                    type="number"
                    min="0"
                    step="0.01"
                    value={macros[field.key]}
                    onChange={(e) => updateMacro(field.key, e.target.value)}
                    placeholder="0"
                  />
                  <span style={{ color: "#516257", fontWeight: 700 }}>{field.unit}</span>
                </div>
              </Field>
            ))}
          </div>
        </Section>

        <Section title="Micros">
          <div style={{ display: "grid", gap: 16, gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", minWidth: 0 }}>
            {MICRO_FIELDS.map((field) => (
              <Field
                key={field.key}
                label={field.label}
                hint="Enter mg, g, or % daily value. Conversion can be derived later."
              >
                <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 10, minWidth: 0 }}>
                  <input
                    style={baseInputStyle()}
                    type="number"
                    min="0"
                    step="0.01"
                    value={micros[field.key].value}
                    onChange={(e) => updateMicroValue(field.key, e.target.value)}
                    placeholder="0"
                  />
                  <select
                    style={baseInputStyle()}
                    value={micros[field.key].unit}
                    onChange={(e) => updateMicroUnit(field.key, e.target.value)}
                  >
                    <option value="mg">mg</option>
                    <option value="g">g</option>
                    <option value="percent_dv">% DV</option>
                  </select>
                </div>
              </Field>
            ))}
          </div>
        </Section>

        {msg && (
          <div style={{
            color: "#8c1d18",
            background: "#fff1ef",
            border: "1px solid rgba(140,29,24,0.15)",
            borderRadius: 12,
            padding: "12px 14px",
          }}>
            {msg}
          </div>
        )}

        <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
          <button
            onClick={handleSave}
            style={{
              border: 0,
              borderRadius: 999,
              padding: "12px 22px",
              background: "#163227",
              color: "#fff",
              fontWeight: 700,
              cursor: "pointer",
            }}
          >
            {saving ? "Saving..." : isEditing ? "Save Changes" : "Save Ingredient"}
          </button>
          <button
            className="secondary"
            onClick={() => navigate(returnTo, {
              replace: true,
              state: { mealDraft: location.state?.mealDraft || storedHandoff?.mealDraft || null },
            })}
            style={{
              borderRadius: 999,
              padding: "12px 22px",
              background: "transparent",
              border: "1px solid rgba(22,50,39,0.18)",
              color: "#163227",
              fontWeight: 700,
              cursor: "pointer",
            }}
          >
            Cancel
          </button>
        </div>
      </div>
    </main>
  );
}
